import { readLegacyMaterials, readRecoveryMaterials, updateRecoveryMaterial } from './blogRecoveryDb'
import { UTM_BUILDER_DEFAULTS } from '../../config/utmBuilderDefaults'
import { BLOG_SYSTEM_AUTHOR_ID } from './blogConstants'

const MATERIALS_KEY = 'blog.local.materials.v1'
const TAGS_KEY = 'blog.local.tags.v1'
const LEGACY_KEYS = {
  [MATERIALS_KEY]: 'ameEdit.local.materials.v1',
  [TAGS_KEY]: 'ameEdit.local.tags.v1',
}

const safeParse = (raw, fallback) => {
  try {
    const value = JSON.parse(raw)
    return value == null ? fallback : value
  } catch (_err) {
    return fallback
  }
}

const readList = (key) => {
  if (typeof window === 'undefined') return []
  try {
    const saved = window.localStorage.getItem(key)
    const raw = saved === null ? window.localStorage.getItem(LEGACY_KEYS[key]) : saved
    const parsed = safeParse(raw, [])
    if (!Array.isArray(parsed)) return []
    if (saved === null && raw !== null) {
      try {
        window.localStorage.setItem(key, raw)
      } catch (_err) {
        // Keep reading the recovery copy even when migration exceeds the quota.
      }
    }
    return parsed
  } catch (_err) {
    return []
  }
}

/**
 * PhotoManager keeps pending uploads as base64 data-URLs in memory.
 * Keep data URLs until uploaded. If storage is full, report failure instead of
 * silently dropping photos from an existing article.
 */
const sanitizePhotoRef = (photo) => {
  if (!photo || typeof photo !== 'object') return null
  const imageUrl = typeof photo.imageUrl === 'string' ? photo.imageUrl : null
  const publicUrl = typeof photo.publicUrl === 'string' ? photo.publicUrl : null
  const url = imageUrl || publicUrl
  if (!url) {
    // Pending local-only blob — cannot persist in localStorage.
    if (photo.storagePath) {
      return {
        imageUrl: null,
        storagePath: photo.storagePath,
        fileName: typeof photo.fileName === 'string' ? photo.fileName : null,
        needUpload: true,
      }
    }
    return null
  }
  return {
    imageUrl: url,
    storagePath: typeof photo.storagePath === 'string' ? photo.storagePath : null,
    fileName: typeof photo.fileName === 'string' ? photo.fileName : null,
  }
}

const sanitizeGalleryItems = (items) => {
  if (!Array.isArray(items)) return []
  return items
    .map((item) => {
      const photo = sanitizePhotoRef(item?.photo)
      if (!photo) return null
      return {
        photo,
        caption: typeof item.caption === 'string' ? item.caption : '',
        alt: typeof item.alt === 'string' ? item.alt : '',
      }
    })
    .filter(Boolean)
}

const sanitizeBlock = (block) => {
  if (!block || typeof block !== 'object') return block
  switch (block.type) {
    case 'image':
    case 'imageText':
      return { ...block, photo: sanitizePhotoRef(block.photo), file: undefined }
    case 'gallery':
      return { ...block, items: sanitizeGalleryItems(block.items) }
    case 'beforeAfter':
      return {
        ...block,
        before: block.before ? { ...block.before, photo: sanitizePhotoRef(block.before.photo) } : block.before,
        after: block.after ? { ...block.after, photo: sanitizePhotoRef(block.after.photo) } : block.after,
      }
    case 'videoUpload':
      return {
        ...block,
        video:
          block.video && typeof block.video === 'object'
            ? {
                url: typeof block.video.url === 'string' ? block.video.url : null,
                storagePath: block.video.storagePath || null,
                fileName: block.video.fileName || null,
              }
            : block.video,
        poster: sanitizePhotoRef(block.poster),
      }
    default:
      return block
  }
}

const sanitizeMaterialForLocalStore = (material) => {
  if (!material || typeof material !== 'object') return material
  const draft = material.draft && typeof material.draft === 'object' ? material.draft : null
  if (!draft) return material
  const seo = draft.seo && typeof draft.seo === 'object' ? draft.seo : null
  return {
    ...material,
    draft: {
      ...draft,
      cover: sanitizePhotoRef(draft.cover),
      seo: seo
        ? {
            ...seo,
            ogImage: sanitizePhotoRef(seo.ogImage),
          }
        : draft.seo,
      blocks: Array.isArray(draft.blocks) ? draft.blocks.map(sanitizeBlock) : [],
    },
  }
}

const writeList = (key, list) => {
  if (typeof window === 'undefined') {
    return { ok: false, reason: 'no-storage' }
  }
  try {
    window.localStorage.setItem(key, JSON.stringify(list))
    return { ok: true }
  } catch (err) {
    const name = err && err.name
    if (name === 'QuotaExceededError' || name === 'NS_ERROR_DOM_QUOTA_REACHED') {
      return { ok: false, reason: 'quota', error: err }
    }
    return { ok: false, reason: 'write-failed', error: err }
  }
}

const createId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `local_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
}

export const listLocalAuthors = () => {
  const owners = Array.isArray(UTM_BUILDER_DEFAULTS.owners) ? UTM_BUILDER_DEFAULTS.owners : []
  return [
    { id: BLOG_SYSTEM_AUTHOR_ID, type: 'system', uid: null, displayName: 'Ame Cosmetics' },
    ...owners
      .filter((owner) => owner?.value)
      .map((owner) => ({
        id: `user:${owner.value}`,
        type: 'user',
        uid: owner.value,
        displayName: owner.label || owner.value,
      })),
  ]
}

export const resolveAuthorLabel = (authorId, authors = listLocalAuthors()) => {
  const match = authors.find((author) => author.id === authorId)
  return match?.displayName || '—'
}

export const listLocalMaterials = async () => {
  let records
  try {
    records = await readRecoveryMaterials()
  } catch (_error) {
    try {
      records = readLegacyMaterials()
    } catch (_readError) {
      records = []
    }
  }
  return records.slice().sort((a, b) => (b.updatedAtSort || 0) - (a.updatedAtSort || 0))
}

export const getLocalMaterial = async (materialId) =>
  (await listLocalMaterials()).find((item) => item.id === materialId) || null

export const removeLocalMaterial = async (materialId) => {
  try {
    await updateRecoveryMaterial(materialId, () => null)
    return { ok: true }
  } catch (error) {
    return { ok: false, reason: 'write-failed' }
  }
}

export const upsertLocalMaterial = async (material) => {
  const now = Date.now()
  const id = material.id || createId()
  const { _localPersist, ...clean } = material
  let next = sanitizeMaterialForLocalStore({
    ...clean,
    id,
    updatedAt: new Date(now).toISOString(),
    updatedAtSort: now,
    createdAt: material.createdAt || new Date(now).toISOString(),
  })
  try {
    next = await updateRecoveryMaterial(id, (existing) => ({
      ...existing,
      ...next,
      createdAt: existing?.createdAt || next.createdAt,
    }))
    return { ...next, _localPersist: { ok: true } }
  } catch (error) {
    return {
      ...next,
      _localPersist: { ok: false, reason: error?.name === 'QuotaExceededError' ? 'quota' : 'write-failed' },
    }
  }
}

export const listLocalTags = () =>
  readList(TAGS_KEY)
    .slice()
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'uk'))

export const getLocalTag = (tagId) => listLocalTags().find((item) => item.id === tagId) || null

export const upsertLocalTag = (tag) => {
  const now = Date.now()
  const list = listLocalTags()
  const id = tag.id || createId()
  const existing = list.find((item) => item.id === id)
  const next = {
    publishedCount: 0,
    kind: 'tag',
    order: 0,
    ...existing,
    ...tag,
    id,
    updatedAt: new Date(now).toISOString(),
    createdAt: existing?.createdAt || tag.createdAt || new Date(now).toISOString(),
  }
  const without = list.filter((item) => item.id !== id)
  writeList(TAGS_KEY, [next, ...without])
  return next
}

export const createLocalId = createId
